<template>
  <main class="account">
    <div class="container">
      <!-- Hero -->
      <section class="card card--hero">
        <div class="hero">
          <div class="avatar" :data-initial="usernameInitial"></div>
          <div class="hero__text">
            <h1>My Account</h1>
            <p class="sub">Welcome, <strong>{{ username }}</strong></p>
  <div class="badges">
  <span v-if="role" class="pill">Role: {{ role }}</span>

  <span v-if="isUnlimitedUser" class="pill pill--ok">Unlimited Access</span>

  <!-- NEW: Annual badge takes precedence over monthly sub (when active) -->
  <span v-else-if="isAnnualActive" class="pill pill--ok">Annual (100/mo) Active</span>

  <span v-else-if="isSubscriptionUser && !hasExpiredSubscription" class="pill pill--ok">Subscription Active</span>
  <span v-else-if="hasExpiredSubscription" class="pill pill--warn">Subscription Expired</span>
    <span v-if="showLinkCheck" class="pill pill--brand" :class="{ 'pill--warn': linkcheckCredits === 0 }">
    LinkCheck: <strong>{{ linkcheckCreditsDisplay }}</strong>
  </span>
</div>

          </div>
        </div>
      </section>
<!-- Profile -->
<section class="card">
  <h2 class="card__title">Profile</h2>
  <p v-if="profileError" class="callout callout--warn">{{ profileError }}</p>

  <div class="stack" v-else>
    <!-- Email row -->
    <div class="row">
      <span class="label">Email</span>

      <!-- Read view -->
      <div v-if="!editingEmail" class="flex gap-2" style="display:flex;align-items:center;gap:.5rem">
        <span>
          <template v-if="email">{{ email }}</template>
          <span v-else class="muted">Not set</span>
        </span>
      <button class="btn btn--ghost small"
        @click="startEditEmail"
        data-track="start_edit_email">
  Edit
</button>

      </div>

      <!-- Edit view -->
      <div v-else class="email-edit" style="display:flex;gap:.5rem;align-items:center;flex-wrap:wrap">
        <input
          v-model.trim="emailDraft"
          type="email"
          inputmode="email"
          autocomplete="email"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="100"
          class="input"
          :class="{ 'input--error': emailError }"
          placeholder="you@example.com"
          @keyup.enter="submitEmail"
        />
      <button class="btn btn--primary"
        :disabled="savingEmail || !!emailError"
        @click="submitEmail"
        data-track="save_email_click"
        :data-track-props="JSON.stringify({ old: email, draft: emailDraft })">
  <span v-if="savingEmail">Saving…</span><span v-else>Save</span>
</button>

     <button class="btn"
        :disabled="savingEmail"
        @click="cancelEditEmail"
        data-track="cancel_edit_email">
  Cancel
</button>
        <span v-if="emailError" class="muted small" style="color:#9b1c1c">{{ emailError }}</span>
        <span v-if="emailSavedFlash" class="muted small" style="color:#1f6b3a">Saved ✔</span>
      </div>
    </div>

    <div class="row" v-if="createdAt">
      <span class="label">Member since</span>
      <span class="nowrap">{{ formatDateTidy(createdAt) }}</span>
    </div>
  </div>

  <p v-if="!email && !editingEmail" class="tiny muted" style="margin-top:.4rem">
    No email on file.
  </p>
</section>


      <!-- Access / Plan -->
      <section class="grid">
      <article class="card">
  <h2 class="card__title">Access</h2>

  <!-- Unlimited -->
  <div v-if="isUnlimitedUser" class="callout callout--ok">
    <strong>Unlimited Access:</strong> Your account has unrestricted access to all tools.
    <p v-if="isSubscriptionUser && subscriptionExpiresAt" class="muted small">
      Note: Your residual subscription expires on {{ formatDate(subscriptionExpiresAt) }}.
    </p>
    <p v-if="isAnnualActive" class="muted small">
      Note: Your annual plan is also active until {{ formatDate(annualExpiresAt) }}.
    </p>
  </div>

  <!-- NEW: Annual plan (100 credits each month) -->
  <template v-else-if="isAnnualActive">
    <div class="stack">
      <div class="row">
        <span class="label">Plan</span>
        <span>Annual ({{ monthlyCreditQuota }} credits / month)</span>
      </div>

      <div class="row">
        <span class="label">Credits</span>
        <span class="mono"><strong>{{ creditBalance }}</strong></span>
      </div>

      <div class="row">
        <span class="label">Monthly refresh</span>
        <span>Refills to {{ monthlyCreditQuota }} on the 1st · <em class="muted">next: {{ nextRefreshLabel }}</em></span>
      </div>

      <div class="row" v-if="annualExpiresAt">
        <span class="label">Annual expires</span>
        <span>
          {{ formatDate(annualExpiresAt) }}
          <em class="muted">· {{ annualDaysLeftMessage }}</em>
        </span>
      </div>

      <!-- Optional success note if this call triggered the refill -->
      <p v-if="refreshedThisCall" class="callout callout--ok">
        Credits refreshed to {{ monthlyCreditQuota }} just now.
      </p>

      <div class="actions" style="margin-top:.35rem">
        <!-- You still allow purchasing extra one-off credits -->
 <router-link class="btn btn--cta btn--xl"
  :to="{ name: 'StripeProductChooser' }"
  data-track="add_credits_click"
  :data-track-props="JSON.stringify({ location:'account_page_annual', balance: creditBalance, plan:'annual' })">
  Add credits
</router-link>
      </div>

      <!-- If you want to surface that a legacy monthly sub is also present -->
      <p v-if="isSubscriptionUser && !hasExpiredSubscription" class="muted tiny">
        You also have a monthly subscription active until {{ formatDate(subscriptionExpiresAt) }}.
      </p>
    </div>
  </template>

  <!-- Monthly subscription (legacy 30-day) -->
  <template v-else>
    <div v-if="isSubscriptionUser && !hasExpiredSubscription" class="stack">
      <div class="row">
        <span class="label">Plan</span>
        <span>Subscription</span>
      </div>
      <div class="row" v-if="subscriptionExpiresAt">
        <span class="label">Expires</span>
        <span>
          {{ formatDate(subscriptionExpiresAt) }}
          <em class="muted">· {{ daysLeftMessage }}</em>
        </span>
      </div>
   <router-link class="btn btn--primary"
  :to="{ name: 'StripeProductChooser' }"
  data-track="extend_subscription_click"
  :data-track-props="JSON.stringify({ location:'account_page_subscription' })">
  Extend subscription
</router-link>
    </div>

    <!-- Pay-as-you-go -->
    <div v-else class="stack">
      <div class="row">
        <span class="label">Status</span>
        <span v-if="hasExpiredSubscription" class="pill pill--warn">Expired</span>
        <span v-else class="pill">Pay-as-you-go</span>
      </div>

      <div class="row" v-if="creditBalance !== null">
        <span class="label">Credits</span>
        <span class="mono"><strong>{{ creditBalance }}</strong></span>
      </div>

      <div v-if="creditBalance !== null" class="actions-row">
     <router-link
  class="btn btn--cta btn--xl"
  :class="{ 'btn--pulse': creditBalance === 0 }"
  :to="{ name: 'StripeProductChooser' }"
  aria-label="Add credits"
  data-track="add_credits_click"
  :data-track-props="JSON.stringify({ location:'account_page_payg', balance: creditBalance, plan:'payg' })">
  Add credits
</router-link>
      </div>

      <p v-else class="muted">Loading credit info…</p>
    </div>
  </template>

  <p v-if="hasExpiredSubscription" class="callout callout--warn">
    ⏳ Your subscription expired on <strong>{{ formatDate(subscriptionExpiresAt) }}</strong>.
  </p>
</article>


        <!-- Referral -->
        <article class="card">
          <h2 class="card__title">Referral code</h2>
          <p class="muted">Share your code to invite others:</p>

          <div class="ref-box">
            <code class="ref-code">{{ referralCode }}</code>
      <button class="btn btn--ghost ref-copy"
  @click="copyReferralCode"
  title="Copy code"
  data-track="copy_referral_code"
  :data-track-props="JSON.stringify({ code: referralCode })">
  <span v-if="copiedReferralCode">✔ Copied</span>
  <span v-else>Copy</span>
</button>
          </div>

          <p class="tiny muted">Codes are non‑sensitive and help us attribute referrals.</p>
        </article>
      </section>
<!-- LinkCheck credits (tool-specific) -->
<article class="card" v-if="showLinkCheck">
  <h2 class="card__title">LinkCheck</h2>

  <div v-if="lcLoading" class="muted small">Loading…</div>
  <p v-else-if="lcError" class="callout callout--warn">{{ lcError }}</p>

  <template v-else>
    <div class="stack">
      <div class="row">
        <span class="label">Credits</span>
        <span class="mono lc-balance">
          <strong>{{ linkcheckCreditsDisplay }}</strong>
        </span>
      </div>

      <div class="row">
        <span class="label">Scope</span>
        <span>Used only for the LinkCheck tool. Independent of main credits/subscriptions.</span>
      </div>

      <div class="actions-row">
        <button class="btn" @click="fetchLinkcheckCredits" :disabled="lcLoading">
          {{ lcLoading ? 'Refreshing…' : 'Refresh' }}
        </button>

        <!-- Optional: wire this when you have a purchase flow for LinkCheck -->
        <router-link
          v-if="showLinkcheckTopUp"
          class="btn btn--cta"
          :to="{ name: 'LinkcheckPurchase' }">
          Add LinkCheck credits
        </router-link>
      </div>

      <p v-if="linkcheckCredits === 0" class="callout callout--warn">
        You have 0 LinkCheck credits. <router-link class="linkish"
  :to="{ name: 'StripeProductChooser' }"
  data-track="add_credits_click_linkcheck"
  :data-track-props="JSON.stringify({ location:'account_page_linkcheck', balance: linkcheckCredits, plan:'linkcheck' })">
  Add credits
</router-link> to run LinkCheck.
      </p>
    </div>
  </template>
</article>
          <!-- Account actions -->
     <section class="card">
  <h2 class="card__title">Security & actions</h2>

  <div class="actions" style="flex-wrap:wrap;gap:.5rem">
    <button
      class="btn linkish"
      @click="goToPasswordReset"
      data-track="open_password_reset">
      🔒 Change password
    </button>

    <button
      v-if="role !== 'admin' && role !== 'moderator'"
      @click="goToDeleteAccountPage"
      class="btn btn--danger"
      data-track="open_delete_account">
      Delete my account
    </button>

    <!-- NEW: bulk delete of analysis request data -->
    <button
      class="btn btn--danger"
      style="margin-left:auto"
      @click="openDeleteHistoryModal"
      :disabled="deleteHistoryCooldownActive"
      data-track="open_delete_request_history">
      <span v-if="deleteHistoryCooldownActive">History deleted ✔</span>
      <span v-else>Delete all analysis request data…</span>
    </button>
  </div>

  <p class="tiny muted" style="margin-top:.4rem">
    This removes your stored analysis request history and related high-quality uploads.
    It does not affect your account, subscriptions, or payments, and it does not refund any credits.
  </p>

  <!-- NEW: Success callout after bulk delete -->
  <p
    v-if="deleteHistorySuccessMessage"
    class="callout callout--ok small"
    style="margin-top:.5rem"
  >
    {{ deleteHistorySuccessMessage }}
  </p>
</section>



<!-- Payment history -->
<section class="card">
  <h2 class="card__title">Payment history</h2>

  <div v-if="phLoading" class="muted small">Loading…</div>
  <p v-if="phError" class="callout callout--warn">{{ phError }}</p>

  <template v-if="!phLoading && !phError">
    <div class="table-wrap">
      <table v-if="paymentHistory.length" class="ph-table">
        <colgroup>
          <col style="width:220px" />   <!-- Time: give it breathing room -->
          <col style="width:110px" />   <!-- Amount -->
          <col style="width:140px" />   <!-- Type -->
          <col style="width:140px" />   <!-- Status -->
          <col />                       <!-- ID grows, but clips inside -->
        </colgroup>
        <thead>
          <tr>
            <th>Time</th>
            <th>Amount</th>
            <th>Type</th>
            <th>Status</th>
            <th>ID</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paymentHistory" :key="item.payment_id">
            <td class="nowrap">{{ formatDateTidy(item.created_at) }}</td>
            <td class="mono">{{ item.amount_added }}</td>
            <td>{{ item.payment_type }}</td>
            <td>
              <span
                class="pill"
                :class="{
                  'pill--ok': item.payment_status === 'succeeded' || item.payment_status === 'paid',
                  'pill--warn': item.payment_status !== 'succeeded' && item.payment_status !== 'paid'
                }"
              >{{ item.payment_status }}</span>
            </td>
           <td>
  <div class="id-row">
  <code class="id-pill mono"
      :class="{ 'id-pill--flash': copiedIds[item.payment_id] }"
      :title="item.payment_id"
      @click="copyPaymentId(item.payment_id)"
      data-track="copy_payment_id"
      :data-track-props="JSON.stringify({ payment_id: item.payment_id })">
  {{ item.payment_id }}
</code>
<button class="btn btn--ghost tiny"
        :disabled="copiedIds[item.payment_id]"
        @click="copyPaymentId(item.payment_id)"
        :title="copiedIds[item.payment_id] ? 'Copied' : 'Copy ID'"
        data-track="copy_payment_id"
        :data-track-props="JSON.stringify({ payment_id: item.payment_id })">
  <span v-if="copiedIds[item.payment_id]">✔ Copied</span>
  <span v-else>Copy</span>
</button>
  </div>

  <!-- accessible live region (hidden visually, announced on copy) -->
  <span class="sr-only" aria-live="polite">
    <template v-if="copiedIds[item.payment_id]">Payment ID copied.</template>
  </span>
</td>

          </tr>
        </tbody>
      </table>

      <p v-else class="muted">No payments yet.</p>
    </div>

    <div v-if="totalPayments > phLimit" class="ph-pagination">
    <button class="btn"
        @click="prevPhPage"
        :disabled="phPage === 1"
        data-track="ph_prev_page"
        :data-track-props="JSON.stringify({ page: phPage, limit: phLimit })">
  Prev
</button>
      <span class="muted small">Page {{ phPage }} of {{ Math.ceil(totalPayments / phLimit) }}</span>
    <button class="btn"
        @click="nextPhPage"
        :disabled="phPage * phLimit >= totalPayments"
        data-track="ph_next_page"
        :data-track-props="JSON.stringify({ page: phPage, limit: phLimit, total: totalPayments })">
  Next
</button>
    </div>
  </template>
</section>

<!-- Quiet support footnote -->
<footer class="account-footnote" aria-label="Support request">
  <span class="muted tiny">Need a hand?</span>
<router-link class="ghost-link tiny"
  :to="{ path: '/support' }"
  rel="nofollow"
  aria-label="Open the Support page to submit a request"
  title="Open Support (requests only)"
  data-track="open_support">
  Request support
</router-link>

</footer>


    </div>

      <!-- Bulk delete request history modal -->
    <teleport to="body">
      <div
        v-if="showDeleteHistoryModal"
        class="report-overlay"
        role="dialog"
        aria-modal="true"
        @click.self="closeDeleteHistoryModal"
      >
        <div class="modal" role="document">
          <header class="modal__head">
            <strong>Delete all analysis request data</strong>
            <button
              class="close"
              @click="closeDeleteHistoryModal"
              aria-label="Close"
            >
              ×
            </button>
          </header>

          <section class="modal__body">
            <p>
              This will permanently delete <strong>all</strong> of your stored analysis
              requests and any associated high-quality uploads.
            </p>
            <p class="muted small">
              You may have paid for some of these requests. Deleting them:
            </p>
            <ul class="muted small">
              <li>Does <strong>not</strong> refund any credits or payments.</li>
              <li>Removes the requests and results from your history.</li>
              <li>Cannot be undone.</li>
            </ul>

         <p class="small" style="margin-top:.75rem">
  To confirm, type <strong>DELETE</strong> in the field below.
</p>

<div class="confirm-delete-field">
  <label
    for="delete-history-confirm"
    class="tiny muted"
  >
    Type <code>DELETE</code> to continue:
  </label>

  <input
    id="delete-history-confirm"
    v-model.trim="deleteHistoryConfirmText"
    class="input input--confirm-delete"
    :disabled="deleteHistoryBusy"
    autocomplete="off"
    spellcheck="false"
    placeholder="DELETE"
  />
</div>


            <p
              v-if="deleteHistoryError"
              class="callout callout--warn"
              style="margin-top:.6rem"
            >
              {{ deleteHistoryError }}
            </p>
          </section>

          <footer class="modal__foot">
            <button
              class="btn"
              @click="closeDeleteHistoryModal"
              :disabled="deleteHistoryBusy"
            >
              Cancel
            </button>
            <button
              class="btn btn--danger"
              :disabled="deleteHistoryBusy || deleteHistoryConfirmText !== 'DELETE'"
              @click="confirmDeleteHistory"
              data-track="confirm_delete_request_history"
            >
              <span v-if="deleteHistoryBusy">Deleting…</span>
              <span v-else>Delete all request data</span>
            </button>
          </footer>
        </div>
      </div>
    </teleport>

  </main>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AccountPage',
  data() {
    return {
      showLinkCheck: false,
      creditBalance: null,
      isSubscriptionUser: false,
      subscriptionExpiresAt: null,
      // NEW: Annual plan
isAnnualCreditUser: false,
annualExpiresAt: null,
monthlyCreditQuota: 100,
    showDeleteHistoryModal: false,
    deleteHistoryConfirmText: '',
    deleteHistoryBusy: false,
    deleteHistoryError: '',
    deleteHistorySuccessMessage: '',
    deleteHistoryCooldownActive: false,
nextRefreshAt: null,
refreshedThisCall: false,
linkcheckCredits: null,
lcLoading: false,
lcError: '',

      role: null,
      isUnlimitedUser: false,
      copiedReferralCode: false,
       email: null,
    createdAt: null,
    profileError: '',
    // payment history state
    paymentHistory: [],
    phPage: 1,
    phLimit: 25,
    totalPayments: 0,
    phLoading: false,
    phError: '',
    copiedIds: {}, // { [payment_id]: true } transient UI state
    editingEmail: false,
    emailDraft: '',
    emailError: '',
    savingEmail: false,
    emailSavedFlash: false,
    // client-side cool-down to deter spam clicking (UX only; server enforces real limit)
    lastEmailSubmitAt: 0
    };
  },
  computed: {
    linkcheckCreditsDisplay() {
  return this.linkcheckCredits === null ? '—' : this.linkcheckCredits;
},
showLinkcheckTopUp() {
  // flip to true once you wire a purchase route for LinkCheck credits
  return false;
},

    username() {
      return localStorage.getItem('username') || 'User';
    },
    usernameInitial() {
      return (this.username || '?').charAt(0).toUpperCase();
    },
referralCode() {
  return `R25${this.simpleHash(this.username.toLowerCase())}`;
},
isAnnualActive() {
  if (!this.isAnnualCreditUser || !this.annualExpiresAt) return false;
  return new Date(this.annualExpiresAt) > new Date();
},
annualDaysLeftMessage() {
  if (!this.annualExpiresAt) return '';
  const now = new Date();
  const expires = new Date(this.annualExpiresAt);
  const diffMs = expires - now;
  if (diffMs <= 0) return 'Expired';
  const daysLeft = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`;
},
nextRefreshLabel() {
  return this.nextRefreshAt ? this.formatDateTidy(this.nextRefreshAt) : '—';
},
    hasExpiredSubscription() {
      if (!this.subscriptionExpiresAt) return false;
      const today = new Date(); today.setHours(0,0,0,0);
      const expiry = new Date(this.subscriptionExpiresAt); expiry.setHours(0,0,0,0);
      return expiry < today;
    },
    daysLeftMessage() {
      if (!this.subscriptionExpiresAt) return '';
      const now = new Date();
      const expires = new Date(this.subscriptionExpiresAt);
      const diffMs = expires - now;
      if (diffMs <= 0) return 'Expired';
      const daysLeft = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      return `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`;
    }
  },
  created() {
     const token = localStorage.getItem('token');
  if (!token) {
    this.$router.replace('/login');
    return;
  }

  axios.get('/etherscan/get_credits.php', {
    headers: { Authorization: `Bearer ${token}` }
  }).then((res) => {
    if (res.data.status === 'success') {
      this.creditBalance = res.data.balance;
      this.subscriptionExpiresAt = res.data.subscription_expires_at;

      const expires = this.subscriptionExpiresAt ? new Date(this.subscriptionExpiresAt) : null;
      if (expires) expires.setHours(0,0,0,0);
      const now = new Date(); now.setHours(0,0,0,0);
      const isExpired = !expires || expires < now;

      this.isSubscriptionUser = !!res.data.is_subscription_user && !isExpired;
      this.role = res.data.role;
      this.isUnlimitedUser = !!res.data.is_unlimited_user;

      // NEW
this.isAnnualCreditUser   = !!res.data.is_annual_credit_user;
this.annualExpiresAt      = res.data.annual_expires_at || null;
this.monthlyCreditQuota   = res.data.monthly_credit_quota || 100;
this.nextRefreshAt        = res.data.next_refresh_at || null;
this.refreshedThisCall    = !!res.data.refreshed_this_call;
    }
  }).catch(err => {
    console.error('Error loading credit info:', err);
  });

  // NEW: profile (email)
  axios.get('/etherscan/get_profile.php', {
    headers: { Authorization: `Bearer ${token}` }
  }).then(res => {
    if (res.data.status === 'success') {
      this.email = res.data.email;          // null or string
      this.createdAt = res.data.created_at; // timestamp or null
    } else {
      this.profileError = res.data.message || 'Failed to load profile.';
    }
  }).catch(err => {
    console.error('Profile error:', err);
    this.profileError = 'Error retrieving profile.';
  });
  // NEW: load payment history
  this.loadPaymentHistory();
  this.fetchLinkcheckCredits();

  },
  mounted() {
    // unify background and keep nav above (prevents “double background”)
    document.body.classList.add('route-account');
  },
  beforeUnmount() {
    document.body.classList.remove('route-account');
  },
  methods: {
      openDeleteHistoryModal() {
    this.deleteHistoryError = '';
    this.deleteHistoryConfirmText = '';
    this.showDeleteHistoryModal = true;
  },

  closeDeleteHistoryModal() {
    if (this.deleteHistoryBusy) return;
    this.showDeleteHistoryModal = false;
    this.deleteHistoryError = '';
    this.deleteHistoryConfirmText = '';
  },

  async confirmDeleteHistory() {
    if (this.deleteHistoryConfirmText !== 'DELETE') {
      this.deleteHistoryError = 'Please type DELETE to confirm.';
      return;
    }

    this.deleteHistoryBusy = true;
    this.deleteHistoryError = '';

    this.deleteHistorySuccessMessage = '';
    try {
      // Axios is already configured elsewhere to send the JWT in Authorization,
      // just like delete_request.php.
      const response = await axios.post('etherscan/delete_all_requests.php', {});

      if (response.data && response.data.status === 'success') {
        // Optional toast, if this helper exists on this component:
        if (typeof this.showNotification === 'function') {
          this.showNotification('All analysis request data has been deleted.', 'success');
        }

        this.showDeleteHistoryModal = false;
        this.deleteHistoryConfirmText = '';
   // Show visible success message under the Security & actions card
        this.deleteHistorySuccessMessage =
          'Your analysis request history and related uploads have been deleted.';
  // Briefly change the button label and disable re-click
        this.deleteHistoryCooldownActive = true;
        setTimeout(() => {
          this.deleteHistoryCooldownActive = false;
        }, 4000);
        // If you keep any request-count badge on this page, you could refresh it here.
        // e.g. this.refreshSomeStats();
      } else {
        this.deleteHistoryError =
          (response.data && response.data.message) ||
          'Failed to delete request history.';
      }
    } catch (err) {
      console.error('Bulk delete request history error:', err);
      this.deleteHistoryError = 'Error deleting history. Please try again.';
    } finally {
      this.deleteHistoryBusy = false;
    }
  },
  // NEW: Annual active flag (true only if not expired)
fetchLinkcheckCredits() {
  const token = localStorage.getItem('token');
  if (!token) { this.lcError = 'Not logged in.'; return; }

  this.lcLoading = true;
  this.lcError = '';

  axios.get('/linkcheck/get_credits.php', {
    headers: { Authorization: `Bearer ${token}` }
  })
  .then(res => {
    if (res.data && res.data.status === 'success') {
      this.linkcheckCredits = Number(res.data.credits);
    } else {
      this.linkcheckCredits = null;
      this.lcError = res.data?.message || 'Failed to load LinkCheck credits.';
    }
  })
  .catch(err => {
    console.error('LinkCheck credits error:', err);
    this.linkcheckCredits = null;
    this.lcError = 'Error retrieving LinkCheck credits.';
  })
  .finally(() => {
    this.lcLoading = false;
  });
},

     startEditEmail() {
    this.emailDraft = this.email || '';
    this.emailError = '';
    this.editingEmail = true;
    this.emailSavedFlash = false;
  },
  cancelEditEmail() {
    this.editingEmail = false;
    this.emailDraft = '';
    this.emailError = '';
  },
  validateEmailFormat(e) {
    if (!e) return 'Email is required';
    if (e.length > 100) return 'Email too long (max 100 chars)';
    // Simple robust pattern (server does final FILTER_VALIDATE_EMAIL)
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(e) ? '' : 'Invalid email format';
  },
  async submitEmail() {
    // client-side cool-down: 5s (server has real 5min + daily cap)
    const now = Date.now();
    if (now - this.lastEmailSubmitAt < 5000) return;
    this.lastEmailSubmitAt = now;

    this.emailError = this.validateEmailFormat(this.emailDraft.trim().toLowerCase());
    if (this.emailError) return;

    if ((this.email || '').toLowerCase() === this.emailDraft.trim().toLowerCase()) {
      this.emailError = 'New email matches your current email';
      return;
    }

    this.savingEmail = true;
    try {
      const res = await axios.post('/etherscan/update_email.php',
        { email: this.emailDraft.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data && res.data.status === 'success') {
        this.email = res.data.email || this.emailDraft.trim().toLowerCase();
        this.editingEmail = false;
        this.emailSavedFlash = true;
        setTimeout(() => (this.emailSavedFlash = false), 1800);
      } else {
        this.emailError = res.data?.message || 'Failed to update email';
      }
    } catch (err) {
      console.error('update_email failed', err);
      // Reflect server-side rate-limit or validation errors if present
      const msg = err?.response?.data?.message;
      this.emailError = msg || 'Error updating email';
    } finally {
      this.savingEmail = false;
    }
  },
    async copyPaymentId(id) {
  try {
    await navigator.clipboard.writeText(id);

    // Vue 3: set reactive object immutably
    this.copiedIds = { ...this.copiedIds, [id]: true };

    setTimeout(() => {
      // remove the key without unused var destructuring
      const next = { ...this.copiedIds };
      delete next[id];
      this.copiedIds = next;
    }, 1500);
  } catch (e) {
    console.error('Copy failed', e);
  }
},

     formatDateTidy(isoString) {
    if (!isoString) return 'N/A';
    // Short, readable, local time. Example: "Aug 16, 2025, 2:07 PM"
    return new Date(isoString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit'
    });
  },
   async copyText(text) {
    try { await navigator.clipboard.writeText(text); }
    catch (e) { console.error('Copy failed', e); }
  },
     loadPaymentHistory() {
    this.phLoading = true;
    this.phError = '';
    axios.post('/etherscan/fetch_my_payment_history.php', {
      page: this.phPage,
      limit: this.phLimit
    }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
    .then(res => {
      if (res.data.status === 'success') {
        this.paymentHistory = res.data.history || [];
        this.totalPayments = res.data.total || 0;
      } else {
        this.phError = res.data.message || 'Failed to load payment history.';
      }
    })
    .catch(err => {
      console.error('Payment history error:', err);
      this.phError = 'Error retrieving payment history.';
    })
    .finally(() => {
      this.phLoading = false;
    });
  },
  nextPhPage() {
    if (this.phPage * this.phLimit < this.totalPayments) {
      this.phPage += 1;
      this.loadPaymentHistory();
    }
  },
  prevPhPage() {
    if (this.phPage > 1) {
      this.phPage -= 1;
      this.loadPaymentHistory();
    }
  },
    goToPasswordReset() { this.$router.push({ name: 'PasswordReset' }); },
    goToDeleteAccountPage() { this.$router.push({ name: 'DeleteAccount' }); },
    copyReferralCode() {
      navigator.clipboard.writeText(this.referralCode).then(() => {
        this.copiedReferralCode = true;
        setTimeout(() => (this.copiedReferralCode = false), 1600);
      }).catch(err => console.error('Failed to copy:', err));
    },
    simpleHash(input) {
      let hash = 0;
      for (let i = 0; i < input.length; i++) { hash = (hash << 5) - hash + input.charCodeAt(i); hash |= 0; }
      return Math.abs(hash).toString(36);
    },
    formatDate(isoString) {
      if (!isoString) return 'N/A';
      // Local timezone, simple readable format
      return new Date(isoString).toLocaleString();
    }
  }
};
</script>

<style scoped>
/* Theme tokens */
.account {
  --bg: #f7f9fc;
  --card: #ffffff;
  --ink: #1f2a37;
  --muted: #6b7280;
  --border: #e5e8ec;
  --accent: #35495e;
  --accent-2: #2c80b4;

  color: var(--ink);
  padding: 1.25rem 1rem 2.5rem;
  background: var(--bg);
}
.container { max-width: 880px; margin: 0 auto; }

/* Cards */
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
  padding: 1.2rem;
  margin-top: 1rem;
}
.card__title { margin: 0 0 .45rem; font-size: 1.05rem; }

/* Hero */
.card--hero {
  margin-top: .25rem;
  background:
    radial-gradient(180px 120px at 85% 20%, rgba(44,128,180,.08), transparent 60%),
    radial-gradient(220px 160px at 10% 90%, rgba(53,73,94,.06), transparent 60%),
    linear-gradient(180deg, #ffffff, #f4f7fc);
  border: 1px solid var(--border);
}
.hero {
  display: grid; grid-template-columns: auto 1fr; gap: .9rem; align-items: center;
}
/* (Optional) tiny polish on the avatar so it pops on the gradient */
.avatar {
  --size: 56px;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  background: #e6eef7;
  color: #1e3a5f;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 1.2rem;
  border: 1px solid var(--border);
  box-shadow: 0 2px 10px rgba(16,24,40,.06);
}
.avatar::after { content: attr(data-initial); }
.hero__text h1 { margin: 0; font-size: clamp(1.4rem, 1.2rem + 1vw, 1.9rem); }
.hero__text .sub { margin: .2rem 0 0; color: var(--muted); }

/* Pills / badges */
.pill {
  display: inline-block;
  padding: .2rem .55rem;
  border-radius: 999px;
  font-size: .78rem;
  font-weight: 700;
  border: 1px solid var(--border);
  background: #f3f6fb; color: #374151;
}
.pill--ok { background: #e9f7ef; color: #1f6b3a; border-color: #cfe9d8; }
.pill--warn { background: #fff6e6; color: #7a3b00; border-color: #f9d7a7; }
.badges { margin-top: .45rem; display: flex; gap: .4rem; flex-wrap: wrap; }

/* Grid */
.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
}
@media (max-width: 820px) { .grid { grid-template-columns: 1fr; } }

/* Stack / rows */
.stack { display: grid; gap: .5rem; }
.row {
  display: flex; justify-content: space-between; align-items: baseline; gap: .75rem;
  padding: .35rem 0; border-bottom: 1px dashed #edf1f6;
}
.row:last-child { border-bottom: none; }
.label { color: var(--muted); min-width: 96px; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace; }

/* Callouts */
.callout {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: .65rem .75rem;
}
.callout--ok { background: #f2fbf6; border-color: #cfe9d8; }
.callout--warn { background: #fff7e6; border-color: #f9d7a7; }

/* Referral box */
.ref-box {
  display: grid; grid-template-columns: 1fr auto; gap: .5rem; align-items: center;
}
.ref-code {
  display: inline-block; padding: .55rem .65rem;
  border: 1px solid var(--border); border-radius: 10px;
  background: #f6f8ff; font-weight: 700;
}
.ref-copy { min-width: 96px; }

/* Buttons */
.btn {
  display: inline-flex; align-items: center; justify-content: center;
  padding: .7rem .95rem; border-radius: 10px; font-weight: 700; cursor: pointer;
  border: 1px solid var(--border); background: #fff; color: var(--accent);
  transition: filter 140ms ease, box-shadow 140ms ease;
}
.btn:hover { filter: brightness(.98); }
.btn--primary {
  border-color: transparent;
  background: linear-gradient(180deg, var(--accent-2), #1f6d9a);
  color: #fff; box-shadow: 0 6px 14px rgba(31,109,154,.18);
}
.btn--danger {
  background: #fff5f5; color: #8f1d1d; border-color: #f4c7c7;
}
.btn--danger:hover { filter: none; }
.linkish {
  background: none; border: none; color: #0b63a9; padding: 0;
}
.linkish:hover { text-decoration: underline; }

/* Helpers */
.muted { color: var(--muted); }
.small { font-size: .92rem; }
.tiny { font-size: .82rem; }
.actions { display: flex; gap: .6rem; flex-wrap: wrap; }


/* Bigger, high-contrast CTA */
.btn--xl {
  padding: 1rem 1.25rem;
  font-size: 1.06rem;
  border-radius: 12px;
}

/* Gradient + glow, but still on-brand */
.btn--cta {
  position: relative;
  border-color: transparent;
  background: linear-gradient(180deg, #5aa7df, #1f6d9a);
  color: #fff;
  box-shadow: 0 10px 18px rgba(31,109,154,.22);
}

/* Hover/keyboard focus delight (no extra text) */
.btn--cta:hover { filter: brightness(1.03); }
.btn--cta:focus-visible {
  outline: 3px solid rgba(44,128,180,.35);
  outline-offset: 2px;
}

/* Gentle attention when balance is exactly 0 (optional) */
@keyframes ctaPulse {
  0% { box-shadow: 0 0 0 0 rgba(31,109,154,.35); }
  100% { box-shadow: 0 0 0 12px rgba(31,109,154,0); }
}
.btn--pulse { animation: ctaPulse 1.8s ease-out infinite; }

/* Add near existing styles in <style scoped> */
.ph-pagination {
  display: flex; gap: .6rem; align-items: center; margin-top: .6rem;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: .4rem;
}
thead th {
  text-align: left;
  font-weight: 700;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
  padding: .45rem .2rem;
}
tbody td {
  padding: .5rem .2rem;
  border-bottom: 1px dashed #edf1f6;
}
/* Keep table in bounds on small screens */
.table-wrap {
  overflow-x: auto;
}

/* Let columns respect colgroup widths */
.ph-table {
  table-layout: fixed;
  width: 100%;
}

/* Slightly roomier table cells for readability */
.ph-table thead th {
  text-align: left;
  font-weight: 700;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
  padding: .55rem .35rem;
}
.ph-table tbody td {
  padding: .55rem .35rem;
  border-bottom: 1px dashed #edf1f6;
  vertical-align: middle;
}

/* Prevent the time column from wrapping to weird multi-line */
.nowrap { white-space: nowrap; }

/* Payment ID: pill style + ellipsis within cell */
.id-row {
  display: flex;
  align-items: center;
  gap: .4rem;
  min-width: 0; /* allows children to shrink for ellipsis */
}

.id-pill {
  display: inline-block;
  max-width: 100%;
  padding: .35rem .5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f6f8ff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Make sure the cell itself allows shrinking */
.ph-table td:last-child { width: 1%; } /* lets the ID cell size to content */

/* Screen-reader only utility */
.sr-only {
  position: absolute !important;
  width: 1px; height: 1px;
  padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0,0,0,0); white-space: nowrap; border: 0;
}

/* Flash feedback on successful copy */
@keyframes pillFlash {
  0%   { background: #e8f9ee; border-color: #bfe7cc; box-shadow: 0 0 0 0 rgba(60,179,113,.25); }
  100% { background: #f6f8ff; border-color: var(--border); box-shadow: 0 0 0 14px rgba(60,179,113,0); }
}
.id-pill--flash {
  animation: pillFlash 900ms ease-out 1;
}

/* Optional: cursor hint if you allow clicking the pill to copy */
.id-pill { cursor: copy; }

/* Disabled copy button subtlety */
button[disabled].tiny {
  opacity: .7;
  cursor: default;
  pointer-events: none;
}
.account-footnote {
  margin-top: 1rem;
  padding-top: .75rem;
  border-top: 1px dashed var(--border);
  display: flex;
  gap: .4rem;
  align-items: baseline;
  justify-content: flex-end;
}

.ghost-link {
  color: var(--muted);
  text-decoration: none;
  border-bottom: 1px dotted rgba(107,114,128,.5);
}
.ghost-link:hover,
.ghost-link:focus-visible {
  color: var(--accent);
  border-bottom-color: currentColor;
  outline: none;
}
.input {
  padding: .55rem .65rem;
  border: 1px solid var(--border);
  border-radius: 10px;
  min-width: 260px;
}
.input--error { border-color: #f4c7c7; background: #fff5f5; }
.btn.small { padding: .45rem .6rem; font-size: .9rem; }

/* brand pill tint distinct from ok/warn */
.pill--brand { border: 1px solid rgba(109,160,255,.45); background: rgba(109,160,255,.12); }

/* big, tidy number */
.lc-balance { font-size: 1.1rem; }

/* Bulk delete history modal – overlay + panel */

.report-overlay {
  position: fixed;
  inset: 0; /* top:0; right:0; bottom:0; left:0; */
  background: rgba(15, 23, 42, 0.45); /* semi-transparent dark backdrop */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2001; /* above nav z-index:1000 from route-account styles */
}

/* Modal shell */
.modal {
  width: min(480px, 100% - 2rem);
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid var(--border);
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.28);
  color: var(--ink);
  display: flex;
  flex-direction: column;
}

/* Header / body / footer layout */
.modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem 0.5rem;
  border-bottom: 1px solid var(--border);
}

.modal__body {
  padding: 0.75rem 1rem 1rem;
}

.modal__foot {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid var(--border);
}

/* Close button in header */
.modal__head .close {
  border: none;
  background: transparent;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}
.confirm-delete-field {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Make the DELETE box look obviously important/dangerous */
.modal__body .input--confirm-delete {
  width: 100%;
  border-color: #f4c7c7;
  background: #fff5f5;
  font-weight: 600;
  letter-spacing: 0.06em;
}

/* Optional: stronger focus ring so you can see it as soon as it appears */
.modal__body .input--confirm-delete:focus-visible {
  outline: 2px solid #b91c1c;
  outline-offset: 2px;
}


</style>

<!-- Route-scoped background & navbar layering (prevents double background & layering issues) -->
<style>
.route-account #app > nav { z-index: 1000; }
.route-account,
.route-account #app,
.route-account #app > main { background: #f7f9fc; }
</style>
